import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Button, Typography } from '@mui/material';
import { getMergePackageName, getPackageName } from '@src/services/package';
import {
  BUSINESS_PACKAGE_CODES,
  FREE_PACKAGE_CODES,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { SUBSCRIPTION_PURCHASE_STATUS } from '@src/constants/subscription';
import {
  checkAutoRenewOrder,
  checkInAppPurchase,
} from '@src/services/subscription';
import { delimitNumber } from '@src/utils/number';
import { CONTACT_US_PAGE } from '@src/configs';
import route from '@src/constants/route';
import PackageV3 from './PackageV3';
import PackageContainer from './components/PackageContainer';
import AutoRenewPackageInfo from './AutoRenewPackageInfo';

const SubscriptionItemV3 = ({
  packageInfo = {},
  usingOrder = {},
  totalRemainingCredits,
  nextResetAt,
  purchasedTopUpCredits = 0,
  isMobile,
  subscriptionPurchase,
  totalCapturedRolloverCredits = 0,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const history = useHistory();
  const { packageExpiryDate, paymentMethod } = usingOrder;
  const { type, code, cycle, topUpPackageCode } = packageInfo;
  const { creditsPerCycle } = cycle || {};
  const { type: paymentMethodType } = paymentMethod || {};

  const isInAppPurchase = checkInAppPurchase(
    packageInfo?.type,
    paymentMethodType,
  );
  const hasExpired =
    packageExpiryDate && moment(packageExpiryDate).isBefore(moment());
  const isFreePackage = FREE_PACKAGE_CODES.includes(code);
  const isBusinessPackage = BUSINESS_PACKAGE_CODES.includes(code);

  const handleRedirectToBuyPackage = () => {
    const redirectUrl = `${route.PAYMENT}`;
    if (type === PACKAGE_TYPE.API) {
      window.open(redirectUrl);
    } else {
      history.push(redirectUrl);
    }
  };

  const hasNextResetCycle = moment(nextResetAt).isBefore(
    packageExpiryDate,
    'day',
  );

  const isMonthlyPackage = cycle?.totalCycles === 1;

  const handleClickContactSupport = () =>
    window.open(CONTACT_US_PAGE, '_blank', 'noopener,noreferrer');

  const handleClickBuyMoreChar = () => {
    const redirectUrl = `${route.PAYMENT}?package=${topUpPackageCode}`;
    history.push(redirectUrl);
  };

  const totalCredits =
    creditsPerCycle + purchasedTopUpCredits + totalCapturedRolloverCredits;
  const usedCredits = totalCredits - totalRemainingCredits;
  const usedCreditsPercent = ((usedCredits / totalCredits) * 100).toFixed(2);

  const renderPackageButton = () => {
    if (isBusinessPackage) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          onClick={handleClickContactSupport}
          data-id="button-buy"
          data-current-package-code={code}
        >
          {t('contact')}
        </Button>
      );
    }

    if (isFreePackage || hasExpired) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          onClick={handleRedirectToBuyPackage}
          data-id="button-buy"
          data-current-package-code={code}
        >
          {t('buyNow')}
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        className="buy-more-btn"
        onClick={handleClickBuyMoreChar}
        data-id="button-buy"
        data-current-package-code={code}
      >
        {t('buyMoreCharacters')}
      </Button>
    );
  };

  const isAutoRenewalPackage =
    usingOrder && checkAutoRenewOrder(usingOrder?.renewalType);

  const isCancelAutoRenew =
    subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.CANCELED;

  const renderDescription = () => (
    <Typography className="credit-per-cycle">
      {isMonthlyPackage && !isAutoRenewalPackage ? (
        <span>
          {t('creditsPerMonth', {
            creditsPerMonth: delimitNumber(creditsPerCycle, language),
          })}
        </span>
      ) : (
        <span>
          {t('creditsPerCycle', {
            creditsPerCycle: delimitNumber(creditsPerCycle, language),
          })}
        </span>
      )}
    </Typography>
  );

  return (
    <>
      <PackageContainer
        packageName={getPackageName(
          packageInfo?.name,
          packageInfo?.code,
          language,
          t,
          true,
        )}
        productType={t(`${getMergePackageName(type)}`)}
        renderDescription={renderDescription}
        renderButton={renderPackageButton}
        packageDetail={
          <>
            <PackageV3
              wallet={{
                usedCredits,
                totalCredits,
                creditsPerCycle,
                nextResetAt,
                usedCreditsPercent,
                hasNextResetCycle,
                packageExpiryDate,
                purchasedTopUpCredits,
              }}
              isMobile={isMobile}
              isMonthlyPackage={isMonthlyPackage}
              isAutoRenewalPackage={isAutoRenewalPackage}
              totalCapturedRolloverCredits={totalCapturedRolloverCredits}
            />
            {(!hasExpired || (!isCancelAutoRenew && !isInAppPurchase)) && (
              <AutoRenewPackageInfo
                packageExpiryDate={packageExpiryDate}
                subscriptionPurchase={subscriptionPurchase}
                packageInfo={packageInfo}
                paymentMethodType={paymentMethodType}
                orderInfo={usingOrder}
                isInAppPurchase={isInAppPurchase}
              />
            )}
          </>
        }
        isMobile={isMobile}
      />
    </>
  );
};

export default SubscriptionItemV3;
